/**
 * Help Portal link helpers — ADR-089, REQ-HLP-004.
 *
 * The Help Portal is a single global instance served at
 * `https://{PLATFORM_DOMAIN}/help/`. The platform domain is derived from the
 * current window location (region-first hostnames per ADR-017/ADR-019) with an
 * optional runtime override, so no FQDN is hardcoded in the frontends.
 */

import type { ReactNode } from 'react';

/**
 * Article slugs of the first content batch (OPS-2026-104/105/112/118/119/125).
 * Keep in sync with docs/help-portal/docs/ — the nav tree is the source of truth.
 */
export const HELP_ARTICLES = {
  GOOGLE_OAUTH: 'identidad/google-oauth-social-login/',
  ENTRA_ID_SSO: 'identidad/entra-id-sso-enterprise/',
  ENTRA_GROUPS_CLAIM: 'identidad/entra-groups-claim-mapeo-roles/',
  GRAPH_PERMISSIONS: 'identidad/permisos-graph-remapeo-roles/',
  GOOGLE_WORKSPACE: 'conectores/google-workspace/',
  MICROSOFT_365: 'conectores/microsoft-365/',
  DOMAIN_DNS_TXT: 'dominios/verificacion-dns-txt/',
  SKILLS_ACTIVATION: 'skills/modos-activacion/',
  RUNNER_INSTALL: 'runner/instalacion/',
  RUNNER_SUPPORT: 'runner/configuracion-soporte/',
} as const;

declare global {
  interface Window {}
}

function getHelpPortalUrlOverride(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const cfg = (window as Window & { __ATLAS_CONFIG__?: Record<string, unknown> }).__ATLAS_CONFIG__;
  const value = cfg?.HELP_PORTAL_URL;
  return typeof value === 'string' ? value : undefined;
}

/**
 * Resolve the platform domain from the current hostname.
 *
 * Region-first hostnames (eu-w1.dev.iovalue.tech, auth-eu.klviar.ai) drop
 * their first label to obtain the platform domain. Hostnames without a
 * region prefix are used as-is.
 */
export function resolvePlatformDomain(hostname?: string): string {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const parts = host.split('.');
  if (parts.length > 2 && /^(auth-)?[a-z]{2}(-[a-z0-9]+)?$/.test(parts[0])) {
    return parts.slice(1).join('.');
  }
  return host;
}

/**
 * Resolve the absolute URL of a help article.
 *
 * Priority: window.__ATLAS_CONFIG__.HELP_PORTAL_URL > derived platform domain.
 * ``hostname`` is an optional testability hook (defaults to window.location).
 */
export function resolveHelpUrl(slug: string, hostname?: string): string {
  const base = getHelpPortalUrlOverride() || `https://${resolvePlatformDomain(hostname)}/help/`;
  const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
  return `${base}${cleanSlug}`;
}

export interface HelpLinkProps {
  /** Article slug (see HELP_ARTICLES) or full path under /help/ */
  slug: string;
  children?: ReactNode;
  className?: string;
  label?: string;
  /**
   * Override completo del `aria-label` (accesibilidad localizada). Si no se
   * indica, se usa el texto por defecto con el `label` recibido.
   */
  ariaLabel?: string;
}

/**
 * Contextual help link — opens the Help Portal article in a new tab.
 * Single shared implementation (admin-console, agent-ui, account-ui).
 */
export function HelpLink({ slug, children, className, label = 'Ayuda', ariaLabel }: HelpLinkProps) {
  return (
    <a
      href={resolveHelpUrl(slug)}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={ariaLabel ?? `${label}: abrir ayuda en una pestaña nueva`}
    >
      {children ?? label}
    </a>
  );
}
